/** React Query hooks for trading data. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tradingApi } from '../services/tradingApi';

export function useTradingAnalyses(symbol?: string) {
  return useQuery({
    queryKey: ['trading-analyses', symbol],
    queryFn: () => tradingApi.getAnalyses({ symbol, limit: 20 }),
    refetchInterval: 30000, // refresh every 30s
  });
}

export function useTradingAnalysis(id: string) {
  return useQuery({
    queryKey: ['trading-analysis', id],
    queryFn: () => tradingApi.getAnalysis(id),
    enabled: !!id,
  });
}

export function useTradingConfirmations(params: { ea_name?: string; symbol?: string } = {}) {
  return useQuery({
    queryKey: ['trading-confirmations', params.ea_name, params.symbol],
    queryFn: () => tradingApi.getConfirmations({ ...params, limit: 20 }),
    refetchInterval: 30000,
  });
}

export function useTradingTrades(symbol?: string) {
  return useQuery({
    queryKey: ['trading-trades', symbol],
    queryFn: () => tradingApi.getTrades({ symbol, limit: 50 }),
    refetchInterval: 60000,
  });
}

export function useTradingTrade(ticket: number) {
  return useQuery({
    queryKey: ['trading-trade', ticket],
    queryFn: () => tradingApi.getTrade(ticket),
    enabled: !!ticket,
  });
}

export function useTradingInsights(status?: string) {
  return useQuery({
    queryKey: ['trading-insights', status],
    queryFn: () => tradingApi.getInsights(status),
  });
}

export function useTradingMetrics() {
  return useQuery({
    queryKey: ['trading-metrics'],
    queryFn: () => tradingApi.getMetrics(),
    refetchInterval: 60000,
  });
}

export function useTradingMetricsHistory(days = 30) {
  return useQuery({
    queryKey: ['trading-metrics-history', days],
    queryFn: () => tradingApi.getMetricsHistory(days),
  });
}

export function useTradingSkills() {
  return useQuery({
    queryKey: ['trading-skills'],
    queryFn: () => tradingApi.getSkills(),
  });
}

export function useTradingConfig() {
  return useQuery({
    queryKey: ['trading-config'],
    queryFn: () => tradingApi.getConfig(),
  });
}

export function useUserTradingConfig() {
  return useQuery({
    queryKey: ['user-trading-config'],
    queryFn: () => tradingApi.getUserConfig(),
  });
}

export function useUpdateUserTradingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: Record<string, unknown>) => tradingApi.updateUserConfig(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-trading-config'] });
    },
  });
}

export function useManualAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { file: File; symbol?: string; timeframe?: string; notes?: string }) =>
      tradingApi.analyzeManual(params.file, params.symbol, params.timeframe, params.notes),
    onSuccess: () => {
      // Invalidate both job queue and analyses (job will complete async)
      queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
    },
  });
}

export function useInsightAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; action: string; notes?: string; modifiedContent?: string; force?: boolean }) =>
      tradingApi.actionInsight(params.id, params.action, params.notes, params.modifiedContent, params.force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-insights'] });
    },
  });
}

export function useUpdateConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: Record<string, unknown>) => tradingApi.updateConfig(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-config'] });
    },
  });
}

export function useTriggerReview() {
  return useMutation({
    mutationFn: (period: 'daily' | 'weekly' | 'monthly') => tradingApi.triggerReview(period),
  });
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { analysisId: string; rating: string; notes?: string; isConfirmation?: boolean }) =>
      params.isConfirmation
        ? tradingApi.submitConfirmationFeedback(params.analysisId, params.rating, params.notes)
        : tradingApi.submitFeedback(params.analysisId, params.rating, params.notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['trading-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['trading-confirmations'] });
      queryClient.invalidateQueries({ queryKey: ['trading-insights'] });
    },
  });
}

export function useValidateSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (analysisId: string) => tradingApi.validateSignal(analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['trading-analysis'] });
    },
  });
}

export function useDeleteSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isConfirmation }: { id: string; isConfirmation: boolean }) =>
      isConfirmation ? tradingApi.deleteConfirmation(id) : tradingApi.deleteAnalysis(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['trading-confirmations'] });
    },
  });
}

export function useCalibrationReport() {
  return useQuery({
    queryKey: ['trading-calibration'],
    queryFn: () => tradingApi.getCalibrationReport(),
  });
}

export function useQuickReviewTrade() {
  return useMutation({
    mutationFn: (ticket: number) => tradingApi.quickReviewTrade(ticket),
  });
}

export function useRepairTradeLinks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tradingApi.repairTradeLinks(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trading-trades'] });
    },
  });
}

export function useBackfillStrategyParameters() {
  return useMutation({
    mutationFn: (dryRun: boolean) => tradingApi.backfillStrategyParameters(dryRun),
  });
}

export function useTestProvider() {
  return useMutation({
    mutationFn: (prompt: string) => tradingApi.testProvider(prompt),
  });
}

export function useAccountLatest() {
  return useQuery({
    queryKey: ['trading-account-latest'],
    queryFn: () => tradingApi.getAccountLatest(),
    refetchInterval: 60000,
  });
}

export function useAccountHistory(days = 30) {
  return useQuery({
    queryKey: ['trading-account-history', days],
    queryFn: () => tradingApi.getAccountHistory(days),
  });
}

export function useAnalysisJobs(params: { status?: string; symbol?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['analysis-jobs', params.status, params.symbol, params.limit],
    queryFn: () => tradingApi.getAnalysisJobs(params),
    refetchInterval: 10000, // poll every 10s
  });
}

export function useRetryJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: number) => tradingApi.retryJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
    },
  });
}

export function useEAMappings() {
  return useQuery({
    queryKey: ['ea-mappings'],
    queryFn: () => tradingApi.getEAMappings(),
  });
}

export function useUsersForMapping() {
  return useQuery({
    queryKey: ['users-for-mapping'],
    queryFn: () => tradingApi.getUsersForMapping(),
  });
}

export function useCreateEAMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { ea_name: string; user_id: number }) => tradingApi.createEAMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-mappings'] });
    },
  });
}

export function useDeleteEAMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eaName: string) => tradingApi.deleteEAMapping(eaName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-mappings'] });
    },
  });
}
